import { useEffect } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from '../../lib/i18n';
import { useBasePath } from '../../lib/use-base-path';
import { useProductName } from '../../lib/use-brand';
import { useGenericBi } from '../../lib/use-generic-bi';
import { logDownloadApp } from '../../lib/appsflyer';
import { APP_SMART_LINK } from '../../lib/app-store-links';
import { APP_BANNER_HEIGHT_PX, mobilePlatform } from '../../lib/app-banner';
import type { MobileAppInstalled } from '../../lib/mobile-app-status';
import { Button, IconButton } from './ui';
import styles from './AppBanner.module.css';

// The mobile-web "get the app" strip — an App-Store-listing-style row pinned to the top
// on narrow viewports, pushing the app down by its own height (AppFrame shrinks <main>
// by `--app-banner-h`). Mirrors Apple's native Smart App Banner (the `apple-itunes-app`
// <meta> in SymphonyApp) on the browsers that banner can't reach: it always shows and its
// CTA changes with install state — "Open" when the app is installed, "Get" otherwise (or
// when unknown). Suppressed on iOS Safari, where Apple's own banner already does exactly this.
//
// No native counterpart, deliberately: like the home's download-the-app QR card, this
// lives in the browser to move someone onto the phone app, which the phone app itself
// has no place for. See orion-web/CLAUDE.md (written-down exception).

const PAGE = 'app_banner';

// Flatten the tri-state install verdict for BI. `null` (we couldn't find out) is its own
// bucket, not folded into "not installed" — a funnel needs to tell the two apart.
const installedTag = (installed: MobileAppInstalled): string =>
  installed === true ? 'true' : installed === false ? 'false' : 'unknown';

export function AppBanner({ installed, onDismiss }: { installed: MobileAppInstalled; onDismiss: () => void }) {
  const { t } = useTranslation();
  const productName = useProductName();
  const basePath = useBasePath();
  const { logPageClickBi, logLoadFinish } = useGenericBi();

  const hasApp = installed === true;

  // Impression: the strip only mounts when shown, so one loadFinish on mount is its
  // "banner seen" beacon (198:5), carrying which CTA the viewer got — matching how the
  // native apps report a sheet/region appearing.
  useEffect(() => {
    logLoadFinish('app-banner', { isSuccessful: true }, { additionalFields: { surface: 'custom-strip', installed: installedTag(installed) } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={styles.banner}
      style={{ height: APP_BANNER_HEIGHT_PX }}
      role="region"
      aria-label={t('getApp.title', { productName })}
      data-app-banner
    >
      <IconButton
        size="small"
        aria-label={t('common.close')}
        className={styles.dismiss}
        onClick={() => {
          logPageClickBi({ page_name: PAGE, button_name: 'dismiss' }, { componentName: 'app-banner-dismiss' });
          onDismiss();
        }}
      >
        <X size={20} aria-hidden />
      </IconButton>
      {/* The app icon, as an App Store row shows it. Decorative — the title names it. */}
      <img src={`${basePath}/300-site-logo.png`} alt="" width={44} height={44} className={styles.icon} />
      <div className={styles.text}>
        <span className={styles.title}>{t('getApp.appName', { productName })}</span>
        <span className={styles.subtitle}>{t('appBanner.subtitle')}</span>
      </div>
      {/* The DS standard (black) button — Design QA (Monday, Figma 168-7694). Was the accent skin. */}
      <Button
        size="small"
        href={APP_SMART_LINK}
        external
        target="_blank"
        rel="noreferrer"
        className={styles.cta}
        onClick={() => {
          logPageClickBi(
            { page_name: PAGE, button_name: hasApp ? 'open_app' : 'get_app' },
            { componentName: hasApp ? 'app-banner-open' : 'app-banner-get', additionalFields: { installed: installedTag(installed) } },
          );
          // The smart link opens the app if installed and routes to the store otherwise, so
          // one href serves both CTAs (as Safari's app-argument does). Only attribute a
          // download when we're not already opening an installed app.
          if (!hasApp) logDownloadApp(mobilePlatform());
        }}
      >
        {hasApp ? t('appBanner.ctaOpen') : t('appBanner.cta')}
      </Button>
    </div>
  );
}
